import React, { useMemo } from "react";
import { fmtCNPJ, fmtCurrency, fmtDateTime } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";

/** Real-time DANFE preview rendered in browser from XML (DOMParser).
 *  Supports:
 *    - procNFe (full NFe) modelo 55  — DANFE A4 layout
 *    - procNFe modelo 65            — Cupom NFC-e style
 *    - resNFe (summary)             — Resumo card with informational notice
 */
const DanfePreview = ({ xml, document: doc }) => {
  const parsed = useMemo(() => parseXml(xml), [xml]);

  if (!xml) {
    return <Empty msg="XML não disponível para este documento." />;
  }
  if (parsed.error) {
    return (
      <Empty msg={`Não foi possível interpretar o XML: ${parsed.error}`} />
    );
  }

  const handlePrint = () => {
    const w = window.open("", "_blank", "width=900,height=1200");
    if (!w) return;
    const html =
      window.document.querySelector('[data-danfe-content="true"]')?.outerHTML ||
      "";
    w.document.write(
      `<html><head><meta charset="utf-8"><title>DANFE ${parsed.chave}</title><style>${printCss()}</style></head><body>${html}</body></html>`,
    );
    w.document.close();
    // The print window has only inline markup and CSS. Triggering from the
    // opener after it is written is more reliable than the popup's load event.
    window.setTimeout(() => {
      if (!w.closed) {
        w.focus();
        w.print();
      }
    }, 350);
  };

  return (
    <div data-testid="danfe-preview">
      <div className="flex items-center justify-between mb-3">
        <div className="label-overline">
          {parsed.kind === "resNFe"
            ? "Documento de Resumo"
            : parsed.modelo === "65"
              ? "DANFE NFC-e"
              : "DANFE NF-e"}
        </div>
        <button
          onClick={handlePrint}
          data-testid="danfe-print-btn"
          className="text-xs rounded-sm border border-border px-2 py-1 hover:bg-muted"
        >
          Imprimir
        </button>
      </div>

      <div
        data-danfe-content="true"
        className="danfe-screen"
      >
        <style>{danfeStyles()}</style>
        {parsed.kind === "resNFe" && <ResumoView p={parsed} doc={doc} />}
        {parsed.kind === "evento" && <EventoView p={parsed} />}
        {parsed.kind === "procNFe" && parsed.modelo === "65" && (
          <NfceView p={parsed} />
        )}
        {parsed.kind === "procNFe" && parsed.modelo !== "65" && (
          <DanfeFullView p={parsed} />
        )}
      </div>
    </div>
  );
};

// ============= Parsing =============

function parseXml(text) {
  if (!text) return { error: "XML vazio" };
  let dom;
  try {
    dom = new DOMParser().parseFromString(text, "text/xml");
  } catch (e) {
    return { error: e.message };
  }
  const perr = dom.getElementsByTagName("parsererror")[0];
  if (perr) return { error: perr.textContent.slice(0, 100) };

  const root = dom.documentElement;
  const local = root.localName;

  if (local === "resNFe") {
    return {
      kind: "resNFe",
      chave: text1(root, "chNFe"),
      cnpj_emit: text1(root, "CNPJ"),
      xNome: text1(root, "xNome"),
      ie: text1(root, "IE"),
      dhEmi: text1(root, "dhEmi"),
      tpNF: text1(root, "tpNF"),
      vNF: parseFloat(text1(root, "vNF") || "0"),
      digVal: text1(root, "digVal"),
      dhRecbto: text1(root, "dhRecbto"),
      cSitNFe: text1(root, "cSitNFe"),
    };
  }

  if (local === "resEvento" || local === "procEventoNFe") {
    // procEventoNFe wraps an evento + retEvento
    const ev = dom.getElementsByTagName("evento")[0] || root;
    const ret = dom.getElementsByTagName("retEvento")[0];
    return {
      kind: "evento",
      chave: text1(ev, "chNFe") || text1(root, "chNFe"),
      cnpj: text1(ev, "CNPJ"),
      tpEvento: text1(ev, "tpEvento") || text1(root, "tpEvento"),
      descEvento: text1(ev, "descEvento") || text1(root, "xEvento") || "Evento",
      dhEvento: text1(ev, "dhEvento") || text1(root, "dhEvento"),
      nSeqEvento: text1(ev, "nSeqEvento"),
      cStat: text1(ret, "cStat") || text1(root, "cStat"),
      xMotivo: text1(ret, "xMotivo") || text1(root, "xMotivo"),
      nProt: text1(ret, "nProt") || text1(root, "nProt"),
    };
  }

  // procNFe / nfeProc
  const infNFe = dom.getElementsByTagName("infNFe")[0];
  if (!infNFe) return { error: "Tag infNFe não encontrada" };

  const id = infNFe.getAttribute("Id") || "";
  const chave = id.startsWith("NFe") ? id.slice(3) : "";

  const ide = first(infNFe, "ide");
  const emit = first(infNFe, "emit");
  const dest = first(infNFe, "dest");
  const total = first(infNFe, "total");
  const transp = first(infNFe, "transp");
  const infAdic = first(infNFe, "infAdic");
  const cobr = first(infNFe, "cobr");
  const dets = Array.from(infNFe.getElementsByTagName("det"));
  const protNFe = dom.getElementsByTagName("protNFe")[0];

  const items = dets.map((det, idx) => {
    const prod = first(det, "prod");
    const imposto = first(det, "imposto");
    const icms = first(imposto, "ICMS");
    const icmsTrib = icms ? Array.from(icms.children)[0] : null;
    const ipi = first(imposto, "IPI");
    return {
      n: det.getAttribute("nItem") || String(idx + 1),
      cProd: text1(prod, "cProd"),
      xProd: text1(prod, "xProd"),
      ncm: text1(prod, "NCM"),
      cfop: text1(prod, "CFOP"),
      uCom: text1(prod, "uCom"),
      qCom: parseFloat(text1(prod, "qCom") || "0"),
      vUnCom: parseFloat(text1(prod, "vUnCom") || "0"),
      vProd: parseFloat(text1(prod, "vProd") || "0"),
      cst: text1(icmsTrib, "CST") || text1(icmsTrib, "CSOSN"),
      vBC: parseFloat(text1(icmsTrib, "vBC") || "0"),
      vICMS: parseFloat(text1(icmsTrib, "vICMS") || "0"),
      pICMS: parseFloat(text1(icmsTrib, "pICMS") || "0"),
      vIPI: parseFloat(text1(ipi, "vIPI") || "0"),
      pIPI: parseFloat(text1(ipi, "pIPI") || "0"),
    };
  });

  const tot = first(total, "ICMSTot");
  const issqnTot = first(total, "ISSQNtot");
  const transporta = first(transp, "transporta");
  const veicTransp = first(transp, "veicTransp");
  const vol = first(transp, "vol");

  return {
    kind: "procNFe",
    chave,
    modelo: text1(ide, "mod"),
    serie: text1(ide, "serie"),
    nNF: text1(ide, "nNF"),
    natOp: text1(ide, "natOp"),
    dhEmi: text1(ide, "dhEmi"),
    dhSaiEnt: text1(ide, "dhSaiEnt"),
    tpNF: text1(ide, "tpNF"),
    tpAmb: text1(ide, "tpAmb"),
    emit: emit
      ? {
          cnpj: text1(emit, "CNPJ"),
          cpf: text1(emit, "CPF"),
          xNome: text1(emit, "xNome"),
          xFant: text1(emit, "xFant"),
          ie: text1(emit, "IE"),
          ieSt: text1(emit, "IEST"),
          crt: text1(emit, "CRT"),
          ender: extractEnder(emit, "enderEmit"),
        }
      : null,
    dest: dest
      ? {
          cnpj: text1(dest, "CNPJ"),
          cpf: text1(dest, "CPF"),
          xNome: text1(dest, "xNome"),
          ie: text1(dest, "IE"),
          indIEDest: text1(dest, "indIEDest"),
          ender: extractEnder(dest, "enderDest"),
        }
      : null,
    items,
    totais: tot
      ? {
          vBC: parseFloat(text1(tot, "vBC") || "0"),
          vICMS: parseFloat(text1(tot, "vICMS") || "0"),
          vBCST: parseFloat(text1(tot, "vBCST") || "0"),
          vST: parseFloat(text1(tot, "vST") || "0"),
          vProd: parseFloat(text1(tot, "vProd") || "0"),
          vFrete: parseFloat(text1(tot, "vFrete") || "0"),
          vSeg: parseFloat(text1(tot, "vSeg") || "0"),
          vDesc: parseFloat(text1(tot, "vDesc") || "0"),
          vOutro: parseFloat(text1(tot, "vOutro") || "0"),
          vIPI: parseFloat(text1(tot, "vIPI") || "0"),
          vPIS: parseFloat(text1(tot, "vPIS") || "0"),
          vCOFINS: parseFloat(text1(tot, "vCOFINS") || "0"),
          vNF: parseFloat(text1(tot, "vNF") || "0"),
        }
      : null,
    modFrete: text1(transp, "modFrete"),
    transportador: transporta
      ? {
          xNome: text1(transporta, "xNome"),
          cnpj: text1(transporta, "CNPJ"),
          cpf: text1(transporta, "CPF"),
          ie: text1(transporta, "IE"),
          xEnder: text1(transporta, "xEnder"),
          xMun: text1(transporta, "xMun"),
          UF: text1(transporta, "UF"),
        }
      : null,
    veiculo: veicTransp
      ? {
          placa: text1(veicTransp, "placa"),
          UF: text1(veicTransp, "UF"),
          rntc: text1(veicTransp, "RNTC"),
        }
      : null,
    volumes: vol
      ? {
          qVol: text1(vol, "qVol"),
          esp: text1(vol, "esp"),
          marca: text1(vol, "marca"),
          nVol: text1(vol, "nVol"),
          pesoL: text1(vol, "pesoL"),
          pesoB: text1(vol, "pesoB"),
        }
      : null,
    faturamento: cobr
      ? {
          numero: text1(first(cobr, "fat"), "nFat"),
          valorOriginal: parseFloat(text1(first(cobr, "fat"), "vOrig") || "0"),
          valorDesconto: parseFloat(text1(first(cobr, "fat"), "vDesc") || "0"),
          valorLiquido: parseFloat(text1(first(cobr, "fat"), "vLiq") || "0"),
          duplicatas: Array.from(cobr.getElementsByTagName("dup")).map((dup) => ({
            numero: text1(dup, "nDup"),
            vencimento: text1(dup, "dVenc"),
            valor: parseFloat(text1(dup, "vDup") || "0"),
          })),
        }
      : null,
    issqn: issqnTot
      ? {
          vServ: parseFloat(text1(issqnTot, "vServ") || "0"),
          vBC: parseFloat(text1(issqnTot, "vBC") || "0"),
          vISS: parseFloat(text1(issqnTot, "vISS") || "0"),
          vPIS: parseFloat(text1(issqnTot, "vPIS") || "0"),
          vCOFINS: parseFloat(text1(issqnTot, "vCOFINS") || "0"),
          im: text1(issqnTot, "IM"),
        }
      : null,
    infCpl: text1(infAdic, "infCpl"),
    protocolo: protNFe
      ? {
          nProt: text1(protNFe, "nProt"),
          dhRecbto: text1(protNFe, "dhRecbto"),
          cStat: text1(protNFe, "cStat"),
          xMotivo: text1(protNFe, "xMotivo"),
          digVal: text1(protNFe, "digVal"),
        }
      : null,
  };
}

function first(parent, tag) {
  if (!parent) return null;
  const els = parent.getElementsByTagName(tag);
  return els.length ? els[0] : null;
}
function text1(parent, tag) {
  if (!parent) return "";
  const els = parent.getElementsByTagName(tag);
  return els.length && els[0].parentNode === parent
    ? els[0].textContent
    : els[0]?.textContent || "";
}
function extractEnder(parent, tag) {
  const e = first(parent, tag);
  if (!e) return null;
  return {
    xLgr: text1(e, "xLgr"),
    nro: text1(e, "nro"),
    xBairro: text1(e, "xBairro"),
    xMun: text1(e, "xMun"),
    UF: text1(e, "UF"),
    CEP: text1(e, "CEP"),
    fone: text1(e, "fone"),
  };
}

const fmtChaveSpaced = (c) => (c || "").replace(/(\d{4})/g, "$1 ").trim();
const fmtCEP = (c) => (c || "").replace(/(\d{5})(\d{3})/, "$1-$2");
const fmtNfeNumber = (number) => String(number || "0").padStart(9, "0");
const fmtSeries = (serie) => String(serie || "0").padStart(3, "0");
const fmtDateOnly = (value) => {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-");
    return `${day}/${month}/${year}`;
  }
  return (fmtDateTime(value).split(",")[0] || "").trim();
};
const fmtTime = (value) => (fmtDateTime(value || "").split(",")[1] || "").trim();
const fmtQuantity = (value) => Number(value || 0).toLocaleString("pt-BR", { maximumFractionDigits: 4 });
const fmtPercent = (value) => `${Number(value || 0).toLocaleString("pt-BR", { maximumFractionDigits: 4 })}%`;
const formatAddress = (address) => {
  if (!address) return "";
  return [address.xLgr, address.nro].filter(Boolean).join(", ");
};
const fmtCpfCnpj = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 14) return fmtCNPJ(digits);
  if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  return value || "";
};
const fretePorConta = (value) => ({
  "0": "0 - Emitente",
  "1": "1 - Destinatário",
  "2": "2 - Terceiros",
  "3": "3 - Remetente",
  "4": "4 - Destinatário",
  "9": "9 - Sem frete",
}[value] || value || "");

// ============= Views =============

const Empty = ({ msg }) => (
  <div
    className="bg-white border border-border rounded-sm p-8 text-center text-sm text-muted-foreground"
    data-testid="danfe-empty"
  >
    {msg}
  </div>
);

const ResumoView = ({ p, doc }) => (
  <div data-testid="danfe-resumo">
    <div className="flex items-start justify-between mb-3 pb-3 border-b border-border">
      <div>
        <div className="text-[10px] uppercase text-gray-500 tracking-[0.08em]">
          Resumo NF-e (resNFe)
        </div>
        <div className="text-base font-semibold mt-1">{p.xNome}</div>
        <div className="text-[11px] text-gray-600">
          {fmtCNPJ(p.cnpj_emit)} · IE {p.ie}
        </div>
      </div>
      <div className="text-right">
        <div className="text-[10px] uppercase text-gray-500">
          Modelo {doc?.modelo} · {p.tpNF === "0" ? "ENTRADA" : "SAÍDA"}
        </div>
        <div className="text-xl font-bold mt-1">{fmtCurrency(p.vNF)}</div>
      </div>
    </div>

    <Field label="Chave de Acesso">{fmtChaveSpaced(p.chave)}</Field>
    <Field label="Emissão">{fmtDateTime(p.dhEmi)}</Field>
    <Field label="Recebimento">{fmtDateTime(p.dhRecbto)}</Field>
    <Field label="Digest Value">{p.digVal}</Field>
    <Field label="Situação NF-e">
      {p.cSitNFe} {p.cSitNFe === "1" ? "· Autorizada" : ""}
    </Field>

    {!doc?.manifestado ? (
      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-sm text-[11px] text-amber-800">
        ℹ️ Este documento contém apenas o <strong>resumo</strong> da NF-e. Para
        liberar o download do XML completo com itens e impostos, feche esta tela
        e clique no ícone do escudo laranja{" "}
        <strong>"Manifestar 210210 (Ciência da Operação)"</strong>.
      </div>
    ) : !doc?.documento_completo ? (
      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-sm text-[11px] text-blue-800">
        ℹ️ A Ciência da Operação já foi realizada! Para visualizar o DANFE
        completo com os itens, feche esta tela e clique no ícone azul de nuvem{" "}
        <strong>"Buscar XML Completo na SEFAZ"</strong>.
      </div>
    ) : null}
  </div>
);

const Field = ({ label, children }) => (
  <div className="grid grid-cols-[160px_1fr] gap-2 py-1.5 border-b border-border/50 text-[11px]">
    <div className="text-gray-500">{label}</div>
    <div className="font-medium break-all">{children || "—"}</div>
  </div>
);

// ----- Evento (resEvento / procEventoNFe) -----
const EVENT_LABELS = {
  210200: "Confirmação da Operação",
  210210: "Ciência da Operação",
  210220: "Desconhecimento da Operação",
  210240: "Operação não Realizada",
  110110: "Carta de Correção",
  110111: "Cancelamento",
};

const EventoView = ({ p }) => {
  const label = EVENT_LABELS[p.tpEvento] || p.descEvento || "Evento";
  return (
    <div data-testid="danfe-evento">
      <div className="text-center pb-3 border-b border-border mb-3">
        <div className="text-[10px] uppercase text-gray-500 tracking-[0.08em]">
          Evento NF-e
        </div>
        <div className="text-lg font-semibold mt-1">{label}</div>
        <div className="text-[11px] text-gray-600">tpEvento {p.tpEvento}</div>
      </div>
      <Field label="Chave NF-e">{fmtChaveSpaced(p.chave)}</Field>
      <Field label="CNPJ Solicitante">{fmtCNPJ(p.cnpj)}</Field>
      <Field label="Data do Evento">{fmtDateTime(p.dhEvento)}</Field>
      <Field label="Sequência">{p.nSeqEvento}</Field>
      <Field label="cStat">{p.cStat}</Field>
      <Field label="Motivo">{p.xMotivo}</Field>
      <Field label="Protocolo">{p.nProt}</Field>
    </div>
  );
};

// ----- DANFE NF-e (modelo 55) -----
const DanfeFullView = ({ p }) => {
  const isEntrada = p.tpNF === "0";
  const duplicatas = p.faturamento?.duplicatas || [];
  return (
    <div data-testid="danfe-full">
      <div className="danfe-paper" role="document" aria-label="DANFE NF-e">
        <div className="danfe-receipt">
          <div className="danfe-receipt-content">
            <div className="danfe-receipt-text">
              RECEBEMOS DE {p.emit?.xNome || ""} OS PRODUTOS E/OU SERVIÇOS
              CONSTANTES DA NOTA FISCAL ELETRÔNICA INDICADA AO LADO
            </div>
            <div className="danfe-receipt-fields">
              <DanfeCell label="DATA DE RECEBIMENTO" />
              <DanfeCell label="IDENTIFICAÇÃO E ASSINATURA DO RECEBEDOR" />
            </div>
          </div>
          <div className="danfe-receipt-number">
            <strong>NF-e</strong>
            <span>Nº {fmtNfeNumber(p.nNF)}</span>
            <span>Série {fmtSeries(p.serie)}</span>
          </div>
        </div>
        <div className="danfe-cut-line" aria-hidden="true" />

        <div className="danfe-header">
          <div className="danfe-issuer">
            <div className="danfe-issuer-name">{p.emit?.xNome}</div>
            {p.emit?.xFant && <div>{p.emit.xFant}</div>}
            <div className="danfe-address">
              {p.emit?.ender?.xLgr}, {p.emit?.ender?.nro}
              {p.emit?.ender?.xBairro ? ` - ${p.emit.ender.xBairro}` : ""}
              <br />
              {p.emit?.ender?.xMun} - {p.emit?.ender?.UF} - CEP {fmtCEP(p.emit?.ender?.CEP)}
              {p.emit?.ender?.fone ? <><br />Fone: {p.emit.ender.fone}</> : null}
            </div>
          </div>
          <div className="danfe-identification">
            <div className="danfe-title">DANFE</div>
            <div className="danfe-subtitle">
              Documento Auxiliar da<br />Nota Fiscal Eletrônica
            </div>
            <div className="danfe-operation">
              <span>{isEntrada ? "0 - ENTRADA" : "1 - SAÍDA"}</span>
              <span className="danfe-operation-marker">{isEntrada ? "0" : "1"}</span>
            </div>
            <div className="danfe-document-number">Nº {fmtNfeNumber(p.nNF)}</div>
            <div>SÉRIE {fmtSeries(p.serie)}</div>
            <div>Página 1 de 1</div>
          </div>
          <div className="danfe-access-key">
            <div className="danfe-barcode"><Code128Barcode value={p.chave} /></div>
            <div className="danfe-field-label">CHAVE DE ACESSO</div>
            <div className="danfe-key-value">{fmtChaveSpaced(p.chave)}</div>
            <div className="danfe-authenticity">
              Consulta de autenticidade no portal nacional da NF-e ou no site da SEFAZ Autorizadora.
            </div>
            <div className="danfe-protocol">
              <div className="danfe-field-label">PROTOCOLO DE AUTORIZAÇÃO DE USO</div>
              {p.protocolo?.nProt} - {fmtDateTime(p.protocolo?.dhRecbto)}
            </div>
          </div>
        </div>

        <DanfeGrid columns="1fr 1fr">
          <DanfeCell label="NATUREZA DA OPERAÇÃO">{p.natOp}</DanfeCell>
          <DanfeCell label="PROTOCOLO DE AUTORIZAÇÃO DE USO">
            {p.protocolo?.nProt} - {fmtDateTime(p.protocolo?.dhRecbto)}
          </DanfeCell>
        </DanfeGrid>
        <DanfeGrid columns="1fr 1.1fr 1fr">
          <DanfeCell label="INSCRIÇÃO ESTADUAL">{p.emit?.ie}</DanfeCell>
          <DanfeCell label="INSCRIÇÃO ESTADUAL DO SUBST. TRIBUT.">{p.emit?.ieSt}</DanfeCell>
          <DanfeCell label="CNPJ">{fmtCpfCnpj(p.emit?.cnpj || p.emit?.cpf)}</DanfeCell>
        </DanfeGrid>

        <DanfeSectionTitle>DESTINATÁRIO / REMETENTE</DanfeSectionTitle>
        <DanfeGrid columns="1.8fr .8fr .52fr">
          <DanfeCell label="NOME / RAZÃO SOCIAL">{p.dest?.xNome}</DanfeCell>
          <DanfeCell label="CNPJ / CPF">{fmtCpfCnpj(p.dest?.cnpj || p.dest?.cpf)}</DanfeCell>
          <DanfeCell label="DATA DA EMISSÃO">{fmtDateOnly(p.dhEmi)}</DanfeCell>
        </DanfeGrid>
        <DanfeGrid columns="1.5fr .62fr .5fr .48fr .52fr">
          <DanfeCell label="ENDEREÇO">{formatAddress(p.dest?.ender)}</DanfeCell>
          <DanfeCell label="BAIRRO / DISTRITO">{p.dest?.ender?.xBairro}</DanfeCell>
          <DanfeCell label="CEP">{fmtCEP(p.dest?.ender?.CEP)}</DanfeCell>
          <DanfeCell label="DATA DA ENTRADA / SAÍDA">{fmtDateOnly(p.dhSaiEnt)}</DanfeCell>
          <DanfeCell label="HORA DA ENTRADA / SAÍDA">{fmtTime(p.dhSaiEnt)}</DanfeCell>
        </DanfeGrid>
        <DanfeGrid columns="1.35fr .42fr .74fr .5fr .55fr">
          <DanfeCell label="MUNICÍPIO">{p.dest?.ender?.xMun}</DanfeCell>
          <DanfeCell label="FONE / FAX">{p.dest?.ender?.fone}</DanfeCell>
          <DanfeCell label="UF">{p.dest?.ender?.UF}</DanfeCell>
          <DanfeCell label="INSCRIÇÃO ESTADUAL">{p.dest?.ie}</DanfeCell>
          <DanfeCell label="INDICADOR IE">{p.dest?.indIEDest}</DanfeCell>
        </DanfeGrid>

        <DanfeSectionTitle>FATURA / DUPLICATA</DanfeSectionTitle>
        <DanfeGrid columns="repeat(3, 1fr)" className="danfe-duplicates">
          {(duplicatas.length ? duplicatas.slice(0, 3) : [null]).map((dup, index) => (
            <DanfeCell key={dup?.numero || index} label={dup ? `DUPLICATA ${dup.numero}` : "FATURA"}>
              {dup ? `${fmtDateOnly(dup.vencimento)}  ${fmtCurrency(dup.valor)}` : p.faturamento?.numero}
            </DanfeCell>
          ))}
        </DanfeGrid>

        <DanfeSectionTitle>CÁLCULO DO IMPOSTO</DanfeSectionTitle>
        <DanfeGrid columns="repeat(5, 1fr)">
          <DanfeCell label="BASE DE CÁLCULO DO ICMS" align="right">{fmtCurrency(p.totais?.vBC)}</DanfeCell>
          <DanfeCell label="VALOR DO ICMS" align="right">{fmtCurrency(p.totais?.vICMS)}</DanfeCell>
          <DanfeCell label="BASE DE CÁLCULO ICMS ST" align="right">{fmtCurrency(p.totais?.vBCST)}</DanfeCell>
          <DanfeCell label="VALOR DO ICMS ST" align="right">{fmtCurrency(p.totais?.vST)}</DanfeCell>
          <DanfeCell label="VALOR TOTAL DOS PRODUTOS" align="right">{fmtCurrency(p.totais?.vProd)}</DanfeCell>
        </DanfeGrid>
        <DanfeGrid columns="repeat(6, 1fr)">
          <DanfeCell label="VALOR DO FRETE" align="right">{fmtCurrency(p.totais?.vFrete)}</DanfeCell>
          <DanfeCell label="VALOR DO SEGURO" align="right">{fmtCurrency(p.totais?.vSeg)}</DanfeCell>
          <DanfeCell label="DESCONTO" align="right">{fmtCurrency(p.totais?.vDesc)}</DanfeCell>
          <DanfeCell label="OUTRAS DESP. ACESSÓRIAS" align="right">{fmtCurrency(p.totais?.vOutro)}</DanfeCell>
          <DanfeCell label="VALOR DO IPI" align="right">{fmtCurrency(p.totais?.vIPI)}</DanfeCell>
          <DanfeCell label="VALOR TOTAL DA NOTA" align="right" strong>{fmtCurrency(p.totais?.vNF)}</DanfeCell>
        </DanfeGrid>

        <DanfeSectionTitle>TRANSPORTADOR / VOLUMES TRANSPORTADOS</DanfeSectionTitle>
        <DanfeGrid columns="1.6fr .62fr .48fr .55fr .28fr .8fr">
          <DanfeCell label="NOME / RAZÃO SOCIAL">{p.transportador?.xNome}</DanfeCell>
          <DanfeCell label="FRETE POR CONTA">{fretePorConta(p.modFrete)}</DanfeCell>
          <DanfeCell label="CÓDIGO ANTT">{p.veiculo?.rntc}</DanfeCell>
          <DanfeCell label="PLACA DO VEÍCULO">{p.veiculo?.placa}</DanfeCell>
          <DanfeCell label="UF">{p.veiculo?.UF}</DanfeCell>
          <DanfeCell label="CNPJ / CPF">{fmtCpfCnpj(p.transportador?.cnpj || p.transportador?.cpf)}</DanfeCell>
        </DanfeGrid>
        <DanfeGrid columns="1.35fr .82fr .28fr .85fr">
          <DanfeCell label="ENDEREÇO">{p.transportador?.xEnder}</DanfeCell>
          <DanfeCell label="MUNICÍPIO">{p.transportador?.xMun}</DanfeCell>
          <DanfeCell label="UF">{p.transportador?.UF}</DanfeCell>
          <DanfeCell label="INSCRIÇÃO ESTADUAL">{p.transportador?.ie}</DanfeCell>
        </DanfeGrid>
        <DanfeGrid columns="repeat(6, 1fr)">
          <DanfeCell label="QUANTIDADE">{p.volumes?.qVol}</DanfeCell>
          <DanfeCell label="ESPÉCIE">{p.volumes?.esp}</DanfeCell>
          <DanfeCell label="MARCA">{p.volumes?.marca}</DanfeCell>
          <DanfeCell label="NUMERAÇÃO">{p.volumes?.nVol}</DanfeCell>
          <DanfeCell label="PESO BRUTO">{p.volumes?.pesoB}</DanfeCell>
          <DanfeCell label="PESO LÍQUIDO">{p.volumes?.pesoL}</DanfeCell>
        </DanfeGrid>

        <DanfeSectionTitle>DADOS DOS PRODUTOS / SERVIÇOS</DanfeSectionTitle>
        <table className="danfe-products">
          <colgroup>
            <col className="danfe-col-code" /><col className="danfe-col-description" /><col className="danfe-col-ncm" />
            <col className="danfe-col-cst" /><col className="danfe-col-cfop" /><col className="danfe-col-unit" />
            <col className="danfe-col-qty" /><col className="danfe-col-value" /><col className="danfe-col-value" />
            <col className="danfe-col-value" /><col className="danfe-col-value" /><col className="danfe-col-value" />
            <col className="danfe-col-rate" /><col className="danfe-col-rate" />
          </colgroup>
          <thead>
            <tr>
              <th>CÓD. PROD.</th><th>DESCRIÇÃO DO PRODUTO / SERVIÇO</th><th>NCM/SH</th><th>CST</th>
              <th>CFOP</th><th>UN.</th><th>QUANT.</th><th>V. UNITÁRIO</th><th>V. TOTAL</th>
              <th>BC ICMS</th><th>V. ICMS</th><th>V. IPI</th><th>ALÍQ. ICMS</th><th>ALÍQ. IPI</th>
            </tr>
          </thead>
          <tbody>
            {p.items.map((it) => (
              <tr key={it.n}>
                <td>{it.cProd}</td><td className="danfe-product-description">{it.xProd}</td><td>{it.ncm}</td><td>{it.cst}</td>
                <td>{it.cfop}</td><td>{it.uCom}</td><td className="danfe-number">{fmtQuantity(it.qCom)}</td>
                <td className="danfe-number">{fmtCurrency(it.vUnCom)}</td><td className="danfe-number">{fmtCurrency(it.vProd)}</td>
                <td className="danfe-number">{fmtCurrency(it.vBC)}</td><td className="danfe-number">{fmtCurrency(it.vICMS)}</td>
                <td className="danfe-number">{fmtCurrency(it.vIPI)}</td><td className="danfe-number">{fmtPercent(it.pICMS)}</td>
                <td className="danfe-number">{fmtPercent(it.pIPI)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <DanfeSectionTitle>CÁLCULO DO ISSQN</DanfeSectionTitle>
        <DanfeGrid columns="repeat(4, 1fr)">
          <DanfeCell label="INSCRIÇÃO MUNICIPAL">{p.issqn?.im}</DanfeCell>
          <DanfeCell label="VALOR TOTAL DOS SERVIÇOS" align="right">{fmtCurrency(p.issqn?.vServ)}</DanfeCell>
          <DanfeCell label="BASE DE CÁLCULO DO ISSQN" align="right">{fmtCurrency(p.issqn?.vBC)}</DanfeCell>
          <DanfeCell label="VALOR DO ISSQN" align="right">{fmtCurrency(p.issqn?.vISS)}</DanfeCell>
        </DanfeGrid>

        <DanfeSectionTitle>DADOS ADICIONAIS</DanfeSectionTitle>
        <DanfeGrid columns="1.7fr .9fr" className="danfe-additional">
          <DanfeCell label="INFORMAÇÕES COMPLEMENTARES">{p.infCpl}</DanfeCell>
          <DanfeCell label="RESERVADO AO FISCO" />
        </DanfeGrid>
        <div className="danfe-footer">
          <span>{p.tpAmb === "2" ? "AMBIENTE DE HOMOLOGAÇÃO - SEM VALOR FISCAL" : "DANFE - Documento Auxiliar da Nota Fiscal Eletrônica"}</span>
          <span>{fmtDateTime(p.protocolo?.dhRecbto || p.dhEmi)}</span>
        </div>
      </div>
    </div>
  );
};

const DanfeSectionTitle = ({ children }) => (
  <div className="danfe-section-title">{children}</div>
);

const DanfeGrid = ({ columns, className = "", children }) => (
  <div className={`danfe-grid ${className}`} style={{ gridTemplateColumns: columns }}>
    {children}
  </div>
);

const DanfeCell = ({ label, children, align = "left", strong = false }) => (
  <div className="danfe-cell">
    <div className="danfe-field-label">{label}</div>
    <div className={`danfe-cell-value danfe-align-${align}${strong ? " danfe-cell-strong" : ""}`}>
      {children || " "}
    </div>
  </div>
);

// Code 128-C encodes the 44 numeric characters of the NF-e access key.
const CODE128_PATTERNS = [
  "212222", "222122", "222221", "121223", "121322", "131222", "122213", "122312", "132212", "221213",
  "221312", "231212", "112232", "122132", "122231", "113222", "123122", "123221", "223211", "221132",
  "221231", "213212", "223112", "312131", "311222", "321122", "321221", "312212", "322112", "322211",
  "212123", "212321", "232121", "111323", "131123", "131321", "112313", "132113", "132311", "211313",
  "231113", "231311", "112133", "112331", "132131", "113123", "113321", "133121", "313121", "211331",
  "231131", "213113", "213311", "213131", "311123", "311321", "331121", "312113", "312311", "332111",
  "314111", "221411", "431111", "111224", "111422", "121124", "121421", "141122", "141221", "112214",
  "112412", "122114", "122411", "142112", "142211", "241211", "221114", "413111", "241112", "134111",
  "111242", "121142", "121241", "114212", "124112", "124211", "411212", "421112", "421211", "212141",
  "214121", "412121", "111143", "111341", "131141", "114113", "114311", "411113", "411311", "113141",
  "114131", "311141", "411131", "211412", "211214", "211232", "2331112",
];

const Code128Barcode = ({ value }) => {
  const numericValue = String(value || "").replace(/\D/g, "");
  if (!numericValue || numericValue.length % 2 !== 0) return null;

  const payload = Array.from({ length: numericValue.length / 2 }, (_, index) =>
    Number(numericValue.slice(index * 2, index * 2 + 2)),
  );
  const checksum = payload.reduce((sum, code, index) => sum + code * (index + 1), 105) % 103;
  const codes = [105, ...payload, checksum, 106];
  let position = 0;
  const bars = [];

  codes.forEach((code) => {
    CODE128_PATTERNS[code].split("").forEach((unit, index) => {
      const width = Number(unit);
      if (index % 2 === 0) bars.push({ position, width });
      position += width;
    });
  });

  return (
    <svg className="danfe-barcode-svg" viewBox={`0 0 ${position} 42`} preserveAspectRatio="none" aria-label="Código de barras da chave de acesso">
      {bars.map((bar, index) => (
        <rect key={`${bar.position}-${index}`} x={bar.position} y="0" width={bar.width} height="42" />
      ))}
    </svg>
  );
};

// ----- NFC-e (modelo 65) cupom -----
const NfceView = ({ p }) => {
  const qrUrl = useMemo(() => {
    // Padrão NFC-e SP (URL de consulta pública)
    const ambienteUF = "35"; // SP
    return `https://www.nfce.fazenda.sp.gov.br/qrcode?p=${p.chave}|2|${ambienteUF}|1|${p.protocolo?.digVal || ""}`;
  }, [p.chave, p.protocolo?.digVal]);

  return (
    <div data-testid="danfe-nfce" className="max-w-sm mx-auto">
      <div className="text-center border-b border-dashed border-gray-400 pb-2 mb-2">
        <div className="font-bold text-[12px]">{p.emit?.xNome}</div>
        <div className="text-[10px]">
          CNPJ {fmtCNPJ(p.emit?.cnpj)} · IE {p.emit?.ie}
        </div>
        {p.emit?.ender && (
          <div className="text-[10px]">
            {p.emit.ender.xLgr}, {p.emit.ender.nro} · {p.emit.ender.xMun}/
            {p.emit.ender.UF}
          </div>
        )}
      </div>
      <div className="text-center text-[11px] font-bold mb-2">DANFE NFC-e</div>
      <div className="text-[10px] text-center mb-2">
        Documento Auxiliar da Nota Fiscal de Consumidor Eletrônica
      </div>

      <table className="w-full text-[10px] border-y border-dashed border-gray-400">
        <thead>
          <tr>
            <th className="text-left">#</th>
            <th className="text-left">Descrição</th>
            <th className="text-right">Qtd</th>
            <th className="text-right">Vl Total</th>
          </tr>
        </thead>
        <tbody>
          {p.items.map((it) => (
            <tr key={it.n}>
              <td>{it.n}</td>
              <td className="truncate max-w-[140px]">{it.xProd}</td>
              <td className="text-right">{it.qCom}</td>
              <td className="text-right">{fmtCurrency(it.vProd)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="text-right text-[12px] font-bold mt-2 pb-2 border-b border-dashed border-gray-400">
        TOTAL R$ {fmtCurrency(p.totais?.vNF).replace("R$", "").trim()}
      </div>

      <div className="flex flex-col items-center mt-3 pt-3 border-t border-dashed border-gray-400">
        <QRCodeSVG value={qrUrl} size={140} level="M" />
        <div className="text-[9px] text-center mt-2 leading-tight">
          Consulte pela Chave de Acesso em
          <br />
          www.fazenda.sp.gov.br/nfce
        </div>
        <div className="font-mono mt-1 text-[9px] break-all text-center">
          {fmtChaveSpaced(p.chave)}
        </div>
        {p.protocolo && (
          <div className="text-[9px] mt-1 text-center">
            Protocolo: {p.protocolo.nProt} · {fmtDateTime(p.protocolo.dhRecbto)}
          </div>
        )}
      </div>
    </div>
  );
};

function danfeStyles() {
  return `
    .danfe-screen {
      overflow-x: auto;
      padding: 10px;
      background: #f4f4f5;
      border: 1px solid #e4e4e7;
      border-radius: 2px;
    }
    .danfe-paper {
      box-sizing: border-box;
      width: 200mm;
      max-width: 100%;
      margin: 0 auto;
      background: #fff;
      color: #000;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 7pt;
      line-height: 1.12;
    }
    .danfe-receipt,
    .danfe-header,
    .danfe-grid,
    .danfe-products,
    .danfe-section-title {
      box-sizing: border-box;
      width: 100%;
    }
    .danfe-receipt {
      display: grid;
      grid-template-columns: 1fr 32mm;
      min-height: 17mm;
      border: .7px solid #000;
    }
    .danfe-receipt-content { min-width: 0; }
    .danfe-receipt-text {
      min-height: 7mm;
      padding: 1.2mm;
      border-bottom: .7px solid #000;
      font-size: 6.2pt;
      font-weight: 700;
      text-transform: uppercase;
    }
    .danfe-receipt-fields { display: grid; grid-template-columns: 32mm 1fr; }
    .danfe-receipt-fields .danfe-cell { min-height: 8mm; }
    .danfe-receipt-number {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: .5mm;
      border-left: .7px solid #000;
      font-size: 8pt;
      font-weight: 700;
    }
    .danfe-receipt-number strong { font-size: 10pt; }
    .danfe-cut-line {
      height: 3mm;
      margin: 1.2mm 0;
      border-top: .7px dashed #000;
    }
    .danfe-header {
      display: grid;
      grid-template-columns: 35% 22% 43%;
      min-height: 42mm;
      border: .7px solid #000;
    }
    .danfe-issuer,
    .danfe-identification,
    .danfe-access-key {
      min-width: 0;
      padding: 2mm;
    }
    .danfe-issuer,
    .danfe-identification { border-right: .7px solid #000; }
    .danfe-issuer-name {
      margin-bottom: 3mm;
      font-size: 11pt;
      font-weight: 700;
      line-height: 1.25;
      text-transform: uppercase;
    }
    .danfe-address { margin-top: 2mm; font-size: 7.2pt; line-height: 1.35; }
    .danfe-identification { text-align: center; }
    .danfe-title { margin-top: 1mm; font-size: 13pt; font-weight: 700; }
    .danfe-subtitle { margin-top: 1mm; font-size: 7.2pt; line-height: 1.16; }
    .danfe-operation {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4mm;
      margin: 3mm 0 2mm;
      font-size: 8pt;
      font-weight: 700;
    }
    .danfe-operation-marker {
      display: inline-flex;
      width: 5mm;
      height: 5mm;
      align-items: center;
      justify-content: center;
      border: .7px solid #000;
    }
    .danfe-document-number { margin-bottom: .8mm; font-size: 8.5pt; font-weight: 700; }
    .danfe-access-key { padding-bottom: 0; }
    .danfe-barcode { height: 14mm; padding: 1mm 0; }
    .danfe-barcode-svg { display: block; width: 100%; height: 100%; fill: #000; }
    .danfe-key-value { margin: .8mm 0 1.5mm; font-family: "Courier New", monospace; font-size: 8.5pt; font-weight: 700; letter-spacing: .25mm; }
    .danfe-authenticity { border-top: .5px solid #999; padding: 1.3mm 0; font-size: 6.3pt; line-height: 1.22; }
    .danfe-protocol { padding-top: 1mm; border-top: .7px solid #000; font-size: 7pt; }
    .danfe-section-title {
      margin-top: 1.3mm;
      padding: .8mm 1.2mm;
      border: .7px solid #000;
      border-bottom: 0;
      font-size: 6.2pt;
      font-weight: 700;
      text-transform: uppercase;
    }
    .danfe-grid {
      display: grid;
      border-left: .7px solid #000;
      border-right: .7px solid #000;
      border-bottom: .7px solid #000;
    }
    .danfe-cell {
      min-width: 0;
      min-height: 7.2mm;
      padding: .8mm 1.2mm;
      border-right: .7px solid #000;
      box-sizing: border-box;
    }
    .danfe-cell:last-child { border-right: 0; }
    .danfe-field-label {
      color: #000;
      font-size: 5.4pt;
      line-height: 1.05;
      text-transform: uppercase;
    }
    .danfe-cell-value {
      min-height: 3mm;
      margin-top: .6mm;
      overflow-wrap: anywhere;
      font-size: 7.2pt;
      font-weight: 500;
      line-height: 1.16;
    }
    .danfe-align-right { text-align: right; }
    .danfe-cell-strong { font-weight: 700; }
    .danfe-duplicates .danfe-cell { min-height: 8mm; }
    .danfe-products {
      table-layout: fixed;
      border-collapse: collapse;
      border: .7px solid #000;
      font-size: 5.9pt;
    }
    .danfe-products th,
    .danfe-products td {
      padding: .8mm .65mm;
      border: .45px solid #000;
      overflow-wrap: anywhere;
      vertical-align: top;
      line-height: 1.15;
    }
    .danfe-products th {
      height: 7mm;
      font-size: 5.3pt;
      font-weight: 700;
      text-align: center;
      vertical-align: middle;
    }
    .danfe-products .danfe-number { text-align: right; white-space: nowrap; }
    .danfe-product-description { white-space: pre-line; }
    .danfe-col-code { width: 6%; }
    .danfe-col-description { width: 28%; }
    .danfe-col-ncm { width: 6%; }
    .danfe-col-cst { width: 4%; }
    .danfe-col-cfop { width: 5%; }
    .danfe-col-unit { width: 4%; }
    .danfe-col-qty { width: 5%; }
    .danfe-col-value { width: 7%; }
    .danfe-col-rate { width: 4%; }
    .danfe-additional .danfe-cell { min-height: 30mm; }
    .danfe-footer {
      display: flex;
      justify-content: space-between;
      gap: 4mm;
      padding-top: 1.2mm;
      font-size: 5.6pt;
    }
    .danfe-footer span:first-child { color: #b91c1c; font-weight: 700; }
    @media (max-width: 700px) {
      .danfe-screen { padding: 4px; }
      .danfe-paper { font-size: 6.2pt; }
      .danfe-issuer-name { font-size: 8.5pt; }
      .danfe-title { font-size: 10pt; }
      .danfe-key-value { font-size: 6.5pt; letter-spacing: .1mm; }
      .danfe-products { font-size: 5pt; }
      .danfe-products th { font-size: 4.6pt; }
    }
  `;
}

// The popup receives only the DANFE markup, so it must also receive the styles
// that normally live in the application document.
function printCss() {
  return `
    @page { size: A4 portrait; margin: 5mm; }
    html, body { margin: 0; padding: 0; background: #fff; }
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    ${danfeStyles()}
    .danfe-screen { padding: 0; border: 0; background: #fff; overflow: visible; }
    .danfe-paper { width: 200mm; max-width: none; margin: 0; }
    .danfe-section-title { break-after: avoid; }
    .danfe-grid, .danfe-products, .danfe-receipt, .danfe-header { break-inside: avoid; }
  `;
}

export default DanfePreview;
